<template>
  <div :class="[{ 'p-error': errorMessage }, { 'c-file-select--disabled': disabled }]">
    <label v-if="$slots.label" class="c-image-upload-label subtitle-2">
      <slot name="label" />
    </label>
    <FileUpload
      ref="uploader"
      customUpload
      :accept="accept"
      :maxFileSize="maxFileSize"
      @select="onFileSelected"
      :disabled="disabled"
      :invalidFileSizeMessage="
        t('errorMessages.fileSize', { maxFileSize }, 'File cannot be bigger than {maxFileSize}')
      "
      :invalidFileTypeMessage="t('errorMessages.fileType', 'File format not supported')"
    />
    <div class="h-[16rem]">
      <div class="c-image-upload-file-container relative w-full mt-2 h-full">
        <button
          type="button"
          :disabled="disabled"
          @dragover="onDragOver"
          @dragleave="onDragLeave"
          @drop="onDrop"
          @click="callFileUpload"
          :class="[
            'c-drop-zone-border w-full max-w-full h-full overflow-x-hidden absolute top-0 lef-0 flex items-center justify-center flex-col',
            { 'c-image-upload-highlight': dragOver },
            { 'c-image-upload-drop-zone': !file },
            { 'cursor-pointer': !disabled },
            { 'cursor-not-allowed': disabled },
          ]"
        >
          <template v-if="!file">
            <CloudUpload class="c-file-select-icon" aria-hidden="true" />
            <span class="c-file-select-title c-file-select-title--link max-w-full">
              {{ t(`fileSelect.uploadFile`) }}
            </span>
            <span class="info-text">
              {{
                t(`fileSelect.filesOnly`, { fileType: `${accept?.replace(', ', ' or ')}` })
              }}</span
            >
          </template>
          <template v-else>
            <FileIcon class="c-file-select-icon" aria-hidden="true" />
            <span
              class="c-file-select-title max-w-full px-6 text-ellipsis overflow-hidden whitespace-nowrap"
            >
              {{ file?.name }}
            </span>
            <span class="info-text">{{ `(${(file?.size / 1024).toFixed(1)}KB)` }} </span>
          </template>
        </button>
      </div>
    </div>

    <div
      id="file-select-error-message"
      class="p-error c-file-select-error flex items-center gap-1 mt-2"
      v-if="errorMessage"
    >
      <CircleAlert class="c-upload-error-icon shrink-0" aria-hidden="true" />
      <span>{{ errorMessage }}</span>
    </div>

    <span v-if="$slots.helperText" class="caption mt-2">
      <slot name="helperText" />
    </span>
    <div class="w-full flex">
      <Button
        v-if="file"
        :label="t(`fileSelect.remove`)"
        :class="`c-button-text mt-2 mx-auto ${ButtonStateClass.TERTIARY} ${ButtonSeverityClass.DANGER} ${ButtonSizeClass.SMALL}`"
        @click="onRemoveClicked"
        icon="pi pi-trash"
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { FileUpload } from './../../../core/file/file-upload';
import { CircleAlert, CloudUpload, File as FileIcon } from 'lucide-vue-next';
import {
  Button,
  ButtonSeverityClass,
  ButtonSizeClass,
  ButtonStateClass,
} from './../../../core/buttons';

const { t } = useI18n();

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  maxFileSize: {
    type: Number,
    default: null,
  },
  accept: {
    type: String,
    default: '.xlsx',
  },
});
const emit = defineEmits(['select', 'removeFile']);

const selectedFile = defineModel('file', {
  type: [File || null],
});

const uploader = ref(null);
const dragOver = ref(false);
const errorMessage = ref(null);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const onDragEnter = (event: Event) => {
  event.stopPropagation();
  event.preventDefault();
};

const onDragOver = (event: Event) => {
  if (!props.disabled) {
    event.stopPropagation();
    event.preventDefault();
    dragOver.value = true;
  }
};

const onDragLeave = () => {
  dragOver.value = false;
};

const onDrop = (event: Event) => {
  if (!props.disabled) {
    event.stopPropagation();
    event.preventDefault();

    dragOver.value = false;
    errorMessage.value = '';

    const files =
      (event as DragEvent).dataTransfer?.files || (event.target as HTMLInputElement).files;

    if (files?.length === 1) {
      uploader.value.onFileSelect(event);
    } else {
      errorMessage.value = t('errorsMessages.fileLimit', 'Only one file can be selected');
    }
  }
};

const onFileSelected = ({ files }: { files: any[] }) => {
  const file = files.length ? files[files.length - 1] : null;

  if (file) {
    selectedFile.value = file;
    errorMessage.value = '';

    if (files.length > 1) {
      uploader.value.remove(0);
    }

    emit('select', file);
  }
};

const removeFile = () => {
  if (selectedFile.value) {
    selectedFile.value = null;
    emit('removeFile');
  }
};

const onRemoveClicked = () => {
  errorMessage.value = null;
  uploader.value.clear();

  removeFile();
};

const callFileUpload = () => {
  uploader.value.choose();
};

watch(
  () => uploader.value?.messages,
  (newMessages, oldMessages) => {
    if (newMessages !== oldMessages && newMessages?.length) {
      errorMessage.value = newMessages[0];
    }
  },
);
</script>
<style scoped lang="scss">
.info-text {
  font-size: var(--typography-body-font-size);
  line-height: var(--typography-body-line-height);
  color: var(--color-text-secondary);
}
.c-drop-zone-border {
  appearance: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: center;
  border: var(--border-width-default) dashed var(--color-forms-border);
  border-radius: var(--radius-role-md);
  background-color: var(--color-surface-canvas);

  &:hover,
  &.c-image-upload-highlight {
    border-color: var(--color-border-strong);
  }

  &:focus-visible {
    outline: none;
    border: var(--border-width-emphasis) solid var(--amber-800);
  }
}
.c-file-select-icon {
  width: var(--size-icon-lg);
  height: var(--size-icon-lg);
  color: var(--color-icon-secondary);
}
.c-file-select-title {
  margin: var(--space-4) 0 0;
  font-size: var(--typography-body-lg-font-size);
  font-weight: var(--font-weight-500);
  line-height: var(--typography-body-lg-line-height);
  color: var(--color-text-primary);

  &--link {
    color: var(--color-text-link);
  }
}
.c-file-select-error {
  font-size: var(--typography-body-font-size);
  line-height: var(--typography-body-line-height);
  color: var(--color-feedback-text-danger);
}
.c-file-select--disabled {
  .c-drop-zone-border {
    background-color: var(--color-disabled-surface);
    border-color: var(--color-forms-border);
  }
  .c-file-select-icon,
  .c-file-select-title,
  .info-text {
    color: var(--color-disabled-content);
  }
}
:deep(.p-fileupload) {
  .p-fileupload-content,
  .p-fileupload-buttonbar button.p-button {
    display: none;
  }
  .p-fileupload-buttonbar {
    display: none;
  }
}
.c-upload-error-icon {
  width: var(--size-icon-sm);
  height: var(--size-icon-sm);
  color: var(--color-feedback-icon-danger);
}
</style>
